/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import {
  Router,
  ActivatedRoute,
  ParamMap, Params
} from '@angular/router';

import {
  FormBuilder,
  FormGroup,
  Validators
}                                from '@angular/forms';

import { first                 } from 'rxjs/operators';

import { Logger                } from '@/util/logger';
import { AuthenticationService } from '@/services/authentication.service';
import { AlertService          } from '@/services/alert.service';
import { getUrlParams }          from '@/util/util';

import { environment } from '../../../environments/environment';

@Component({
  standalone: false,
  templateUrl: 'login.component.html'
})

/**
 *
 * @class LoginComponent
 *
 * @brief we show the login page when there is no established user session
 *
 * Angular tutorial on login / registration.
 * @link https://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial#app-component-html
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class LoginComponent implements OnInit {

  /** use a rand background image for the page */
  public backgrounds: string[] = ['rail1-1280x720.png', 'rail2-1280x720.png', 'rail3-1280x720.png', 'rail4-1280x720.png'];
  public background: string    = '';

  /** the external links for jumping to an OAuth delegated login */
  public google_sso: string    = environment.google_sso;
  public microsoft_sso: string = environment.microsoft_sso;

  /** form validation */
  public loginForm: FormGroup;

  /** flag to tell the form when we are processing */
  public loading:   boolean   = false;

  /* flag to tell the form that we completed an authentication request */
  public submitted: boolean   = false;

  /** where to return to once authenticated */
  public returnUrl: string    = '/';

  /** when we finalize SSO login, we have to authenticate with a one-time code instead of userid/password */
  public authCode: string = '';

  /** if we get redirected to login, *and* we had query parameters, try to pass 'em along */
  public queryParams: any;

  /** standard constructor */
  constructor(
    private formBuilder:  FormBuilder,
    private route:        ActivatedRoute,
    private router:       Router,
    private auth:         AuthenticationService,
    private alerts:       AlertService,
    private cdRef:        ChangeDetectorRef
  ) {

    let lc: LoginComponent = this;

    /* pick a random background */

    lc.background = lc.backgrounds[Math.floor(Math.random() * lc.backgrounds.length)];

    Logger.debug('[Login] checking if already logged in...');

    lc.returnUrl   = lc.route.snapshot.queryParamMap.get('returnUrl') || '/';
    lc.queryParams = getUrlParams(lc.route.snapshot.url);
    lc.authCode    = lc.route.snapshot.queryParamMap.get('auth_code') || '';

    /* use the web browser to pull out the 'uri' of the return path */

    const parser     = document.createElement('a');
    parser.href    = lc.returnUrl;
    lc.returnUrl   = parser.pathname;

    /* if we have an authcode, then we can try to auto-signin that SSO user ... */

    if (lc.authCode.length > 0) {

      Logger.debug('[Login] authenticating: ', lc.authCode);

      lc.auth.loginSSO(lc.authCode)
        .pipe(first())
        .subscribe({

          next: (data: any): void => {

            Logger.debug('[Login] authenticated! returning to: /overwatch :', data);

            lc.router.navigate(['/overwatch']);
          },

          error: (error: any): void => {

            lc.alerts.error(error.error.message, {autoClose: true});

            Logger.error('[Login] authentication error: ', error.error.message);

            lc.loading = false;

            lc.cdRef.detectChanges();

          }

        });
    }

    /* are we already logged in? */

    if (lc.auth.userOfSession) {

      Logger.debug('[Login] already logged in, going back to ', lc.returnUrl, lc.queryParams);

      lc.router.navigate(
        [lc.returnUrl],
        {
          queryParams: lc.queryParams,
          queryParamsHandling: 'preserve'
        });
    }
  }

  /** on page setup, create the form validator */
  ngOnInit() {

    Logger.debug('[Login] init...');

    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    /* return to - should be where they were before, or home. */

    Logger.debug('[Login] will return to: ', this.returnUrl, this.queryParams);
  }

  /** easy access to form fields */
  get f() {

    return this.loginForm.controls;
  }

  /** form submission (authentication attempt) */
  onSubmit() {

    let lc: LoginComponent = this;

    lc.submitted = true;

    lc.cdRef.detectChanges();

    /* do we have all fields? */

    if (lc.loginForm.invalid) {

      return;
    }

    lc.loading = true;

    lc.cdRef.detectChanges();

    Logger.debug('[Login] authenticating: ', lc.f.username.value, lc.f.password.value);

    lc.auth.login(lc.f.username.value, lc.f.password.value)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          Logger.debug('[Login] authenticated! returning to: ', lc.returnUrl, data);
          Logger.debug('[Login] query params:', lc.queryParams);

          lc.router.navigate(
            [lc.returnUrl],
            {
              queryParams: lc.queryParams,
              queryParamsHandling: 'merge'
            });
        },

        error: (error: any): void => {

          lc.alerts.error(error.error.message, {autoClose: true});

          Logger.error('[Login] authentication error: ', error.error.message);

          lc.loading = false;

          lc.cdRef.detectChanges();
        }

      });
  }

}
